import { Avatar, type AvatarProps } from '@mantine/core';
import {
  getAvatarColor,
  getInitialsFromParts,
} from '../../utils/avatarHelpers';

interface ParticipantAvatarProps extends Omit<
  AvatarProps,
  'color' | 'children'
> {
  firstName: string;
  lastName: string;
  /** Optional identifier for color generation (defaults to name) */
  colorKey?: string;
  /**
   * Optional vertical nudge (px) for the initials, for fine optical centering
   * in a given layout. Opt-in so other consumers are unaffected.
   */
  initialsOffsetY?: number;
}

/**
 * Participant avatar with deterministic colors based on name.
 * Always shows the same color for the same participant.
 */
export function ParticipantAvatar({
  firstName,
  lastName,
  colorKey,
  size = 40,
  radius = 'xl',
  initialsOffsetY,
  ...props
}: ParticipantAvatarProps) {
  const key = colorKey || `${firstName}${lastName}`;
  const backgroundColor = getAvatarColor(key);
  const initials = getInitialsFromParts(firstName, lastName);

  return (
    <Avatar
      size={size}
      radius={radius}
      styles={{
        root: { backgroundColor },
        placeholder: {
          color: '#FFFFFF',
          backgroundColor,
          ...(initialsOffsetY
            ? { transform: `translateY(${initialsOffsetY}px)` }
            : {}),
        },
      }}
      {...props}
    >
      {initials}
    </Avatar>
  );
}

export default ParticipantAvatar;
